/**
 * WalletHistoryPanel.jsx
 *
 * Redesigned History → Wallet view in the "analysis page" aesthetic:
 * JetBrains Mono, dark panel palette, KPI strip, and an interactive
 * cumulative P/L area chart (SOL-denominated realized PnL).
 *
 * Props:
 *   history        array of trade records from useTradeHistory
 *   loading        bool
 *   publicKey      wallet pubkey (or null)
 *   onRefresh()    force re-pull (clears cache upstream)
 *   onSelectMint(mint)   subscribe to a token on row click
 *   formatCompact  number formatter passed from parent
 */

import { useMemo, useRef, useState, useLayoutEffect } from 'react'

const C = {
  bg: '#0a0e14', panel: '#10151f', panel2: '#0d121b',
  line: '#1d2633', lineSoft: '#161d28',
  txt: '#c3ccd9', dim: '#5b6677', dimmer: '#3a4452',
  green: '#46d41f', greenSoft: '#2a7a18',
  red: '#ef4444', amber: '#f5a623', cyan: '#36c5d4',
}
const MONO = "'JetBrains Mono', ui-monospace, monospace"

function useSize() {
  const ref = useRef(null)
  const [size, setSize] = useState({ width: 0, height: 0 })
  useLayoutEffect(() => {
    if (!ref.current) return
    const ro = new ResizeObserver(([e]) => {
      const r = e.contentRect
      setSize({ width: r.width, height: r.height })
    })
    ro.observe(ref.current)
    return () => ro.disconnect()
  }, [])
  return [ref, size]
}

const fmtSol = v => `${v >= 0 ? '+' : ''}${v.toFixed(2)}`
const fmtRatio = v => !Number.isFinite(v) ? '∞' : v.toFixed(2)

// Resample a polyline to `n` evenly-spaced X samples via linear interpolation
// between the real points, then apply a light moving-average to the Y values.
// Faithful (the line still passes through the real trajectory; no overshoot
// or invented dips), but removes the per-trade micro-kinks so the subsequent
// cubic reads as a smooth, flowing curve.
function resampleEven(coords, n) {
  if (coords.length < 3) return coords
  const x0 = coords[0][0], x1 = coords[coords.length - 1][0]
  const span = x1 - x0
  if (span <= 0) return coords
  const out = []
  let j = 0
  for (let i = 0; i < n; i++) {
    const x = x0 + (span * i) / (n - 1)
    while (j < coords.length - 2 && coords[j + 1][0] < x) j++
    const [ax, ay] = coords[j]
    const [bx, by] = coords[j + 1] || coords[j]
    const t = bx === ax ? 0 : (x - ax) / (bx - ax)
    out.push([x, ay + (by - ay) * t])
  }
  // Light moving average (window 3) on Y to soften residual stair-steps.
  const sm = out.map((p, i) => {
    if (i === 0 || i === out.length - 1) return p
    return [p[0], (out[i - 1][1] + p[1] + out[i + 1][1]) / 3]
  })
  return sm
}

// Monotone cubic interpolation — produces a smooth curve that NEVER
// overshoots the data. Critical for a P/L line: a plain Catmull-Rom or
// quadratic smoothing can bow the curve below the last point and render a
// dip (a loss) that never happened. Monotone tangents are clamped so each
// segment stays within its endpoints' value range.
function smoothPath(pts) {
  const n = pts.length
  if (n < 2) return n ? `M${pts[0][0].toFixed(1)},${pts[0][1].toFixed(1)}` : ''
  if (n === 2) {
    return `M${pts[0][0].toFixed(1)},${pts[0][1].toFixed(1)} L${pts[1][0].toFixed(1)},${pts[1][1].toFixed(1)}`
  }
  const xs = pts.map(p => p[0])
  const ys = pts.map(p => p[1])
  const dx = [], dy = [], slope = []
  for (let i = 0; i < n - 1; i++) {
    dx[i] = xs[i + 1] - xs[i]
    dy[i] = ys[i + 1] - ys[i]
    slope[i] = dx[i] !== 0 ? dy[i] / dx[i] : 0
  }
  // tangents (Fritsch–Carlson)
  const m = new Array(n)
  m[0] = slope[0]
  m[n - 1] = slope[n - 2]
  for (let i = 1; i < n - 1; i++) {
    if (slope[i - 1] * slope[i] <= 0) m[i] = 0
    else m[i] = (slope[i - 1] + slope[i]) / 2
  }
  for (let i = 0; i < n - 1; i++) {
    if (slope[i] === 0) { m[i] = 0; m[i + 1] = 0; continue }
    const a = m[i] / slope[i]
    const b = m[i + 1] / slope[i]
    const hyp = a * a + b * b
    if (hyp > 9) {
      const t = 3 / Math.sqrt(hyp)
      m[i] = t * a * slope[i]
      m[i + 1] = t * b * slope[i]
    }
  }
  let d = `M${xs[0].toFixed(1)},${ys[0].toFixed(1)}`
  for (let i = 0; i < n - 1; i++) {
    const h = dx[i]
    const x1 = xs[i] + h / 3
    const y1 = ys[i] + m[i] * h / 3
    const x2 = xs[i + 1] - h / 3
    const y2 = ys[i + 1] - m[i + 1] * h / 3
    d += ` C${x1.toFixed(1)},${y1.toFixed(1)} ${x2.toFixed(1)},${y2.toFixed(1)} ${xs[i + 1].toFixed(1)},${ys[i + 1].toFixed(1)}`
  }
  return d
}

// ─── Interactive cumulative P/L area chart ───────────────────────────
function PnlChart({ points, onHoverTime, unit = 'SOL' }) {
  const [wrapRef, { width, height }] = useSize()
  const [hover, setHover] = useState(null)

  const geo = useMemo(() => {
    if (!points || points.length < 2 || width <= 0 || height <= 0) return null
    const padT = 10, padB = 16, padL = 0, padR = 0
    const w = width - padL - padR
    const h = height - padT - padB
    const xs = points.map(p => p.time)
    const ys = points.map(p => p.pnl)
    const minX = xs[0], maxX = xs[xs.length - 1]
    let minY = Math.min(0, ...ys), maxY = Math.max(0, ...ys)
    if (maxY === minY) { maxY += 1; minY -= 1 }
    const sx = t => padL + ((t - minX) / (maxX - minX || 1)) * w
    const sy = v => padT + (1 - (v - minY) / (maxY - minY || 1)) * h
    const zeroY = sy(0)
    const coords = points.map(p => [sx(p.time), sy(p.pnl)])
    // Resample to evenly-spaced X samples before smoothing. The raw series has
    // one point per trade at uneven timestamps, which makes even a cubic look
    // kinky. Sampling the cumulative P/L at uniform X (linear-interpolated
    // between real points — so it stays faithful, no invented dips) and then
    // running monotone cubic over THAT yields a clean, flowing line.
    const smoothCoords = resampleEven(coords, Math.min(120, Math.max(24, coords.length)))
    const line = smoothPath(smoothCoords)
    const last = points[points.length - 1].pnl
    const up = last >= 0
    const area = `${line} L${smoothCoords[smoothCoords.length - 1][0].toFixed(1)},${zeroY.toFixed(1)} L${smoothCoords[0][0].toFixed(1)},${zeroY.toFixed(1)} Z`
    return { coords, line, area, zeroY, up, minY, maxY, padT, h, sx, sy, minX, maxX }
  }, [points, width, height])

  const onMove = e => {
    if (!geo) return
    const rect = e.currentTarget.getBoundingClientRect()
    const x = e.clientX - rect.left
    // nearest point
    let best = 0, bd = Infinity
    geo.coords.forEach(([cx], i) => { const d = Math.abs(cx - x); if (d < bd) { bd = d; best = i } })
    setHover(best)
    onHoverTime?.(points[best].time)
  }
  const onLeave = () => { setHover(null); onHoverTime?.(null) }

  const col = geo?.up ? C.green : C.red
  const gid = geo?.up ? 'pnlUp' : 'pnlDn'

  return (
    <div ref={wrapRef} style={{ width: '100%', height: '100%', position: 'relative' }}>
      {geo && (
        <svg width={width} height={height} style={{ display: 'block' }}
          onMouseMove={onMove} onMouseLeave={onLeave}>
          <defs>
            <linearGradient id={gid} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={col} stopOpacity="0.22" />
              <stop offset="100%" stopColor={col} stopOpacity="0" />
            </linearGradient>
          </defs>
          {/* zero baseline */}
          <line x1="0" y1={geo.zeroY} x2={width} y2={geo.zeroY}
            stroke={C.line} strokeWidth="1" strokeDasharray="3 3" />
          <path d={geo.area} fill={`url(#${gid})`} />
          <path d={geo.line} fill="none" stroke={col} strokeWidth="1.6"
            strokeLinejoin="round" strokeLinecap="round" />
          {/* hover marker */}
          {hover != null && (
            <>
              <line x1={geo.coords[hover][0]} y1={geo.padT} x2={geo.coords[hover][0]} y2={geo.padT + geo.h}
                stroke={C.dimmer} strokeWidth="1" />
              <circle cx={geo.coords[hover][0]} cy={geo.coords[hover][1]} r="3" fill={col}
                stroke={C.bg} strokeWidth="1.5" />
            </>
          )}
        </svg>
      )}
      {geo && hover != null && (
        <div style={{
          position: 'absolute', top: 4,
          left: Math.min(Math.max(geo.coords[hover][0] + 8, 4), width - 110),
          background: C.panel2, border: `1px solid ${C.line}`, borderRadius: 4,
          padding: '4px 8px', fontFamily: MONO, fontSize: 10, pointerEvents: 'none',
          whiteSpace: 'nowrap', color: C.txt,
        }}>
          <div style={{ color: points[hover].pnl >= 0 ? C.green : C.red, fontWeight: 700 }}>
            {fmtSol(points[hover].pnl)} {unit}
          </div>
          <div style={{ color: C.dim, fontSize: 9 }}>
            {new Date(points[hover].time).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
          </div>
        </div>
      )}
    </div>
  )
}

function Kpi({ label, val, note, tone }) {
  const barCol = tone === 'good' ? C.green : tone === 'bad' ? C.red : tone === 'warn' ? C.amber : C.dimmer
  const valCol = tone === 'good' ? C.green : tone === 'bad' ? C.red : '#eef3f8'
  return (
    <div style={{
      background: `linear-gradient(160deg, ${C.panel}, ${C.panel2})`,
      border: `1px solid ${C.line}`, borderRadius: 7, padding: '9px 11px',
      position: 'relative', overflow: 'hidden', minWidth: 0,
    }}>
      <div style={{
        position: 'absolute', left: 0, top: 0, bottom: 0, width: 10,
        background: barCol,
        clipPath: 'polygon(0 0, 100% 0, 0 100%)',
      }} />
      <div style={{ color: C.dim, fontSize: 9, letterSpacing: '.6px', textTransform: 'uppercase' }}>{label}</div>
      <div style={{ color: valCol, fontSize: 17, fontWeight: 800, marginTop: 3, letterSpacing: '-.3px' }}>{val}</div>
      {note && <div style={{ color: C.dimmer, fontSize: 9, marginTop: 1 }}>{note}</div>}
    </div>
  )
}

// ─── Donut ring for a single percentage (win rate / profit rate) ──────
function Ring({ pct, label, sub, color }) {
  const r = 30, c = 2 * Math.PI * r
  const p = Math.max(0, Math.min(100, pct || 0))
  const dash = (p / 100) * c
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3 }}>
      <svg width="72" height="72" viewBox="0 0 72 72">
        <circle cx="36" cy="36" r={r} fill="none" stroke={C.line} strokeWidth="7" />
        <circle cx="36" cy="36" r={r} fill="none" stroke={color} strokeWidth="7"
          strokeDasharray={`${dash} ${c}`} strokeLinecap="round"
          transform="rotate(-90 36 36)" />
        <text x="36" y="36" textAnchor="middle" dominantBaseline="central"
          fill={C.txt} fontSize="15" fontWeight="700" fontFamily={MONO}>{p.toFixed(1)}%</text>
      </svg>
      <div style={{ color: C.dim, fontSize: 9, letterSpacing: '.5px', textTransform: 'uppercase' }}>{label}</div>
      {sub && <div style={{ color: C.dimmer, fontSize: 9 }}>{sub}</div>}
    </div>
  )
}

// ─── Labeled stat cell (value + caption), with optional tone color ────
function StatCell({ label, value, tone }) {
  const col = tone === 'good' ? C.green : tone === 'bad' ? C.red : C.txt
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      <div style={{ color: col, fontSize: 13, fontWeight: 700, fontFamily: MONO }}>{value}</div>
      <div style={{ color: C.dim, fontSize: 8.5, letterSpacing: '.4px', textTransform: 'uppercase' }}>{label}</div>
    </div>
  )
}

// ─── Distribution of round-trip % returns (histogram) ─────────────────
function DistHistogram({ dist, buckets }) {
  const max = Math.max(1, ...dist)
  const labelFor = (i) => {
    if (i === 0) return `<${buckets[0]}`
    if (i === buckets.length) return `${buckets[buckets.length - 1]}+`
    return `${buckets[i - 1]}`
  }
  return (
    <div style={{ display: 'flex', alignItems: 'flex-end', gap: 2, height: 70 }}>
      {dist.map((v, i) => {
        const neg = i < buckets.indexOf(0)
        const col = neg ? C.red : C.cyan
        return (
          <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, height: '100%', justifyContent: 'flex-end' }}
            title={`${labelFor(i)}%: ${v} trade${v !== 1 ? 's' : ''}`}>
            {v > 0 && <span style={{ color: C.dim, fontSize: 7 }}>{v}</span>}
            <div style={{ width: '100%', height: `${(v / max) * 100}%`, minHeight: v > 0 ? 2 : 0, background: col, borderRadius: '2px 2px 0 0', opacity: 0.85 }} />
          </div>
        )
      })}
    </div>
  )
}

// ─── Monthly P/L heatmap calendar ────────────────────────────────────
const DOW = ['S', 'M', 'T', 'W', 'T', 'F', 'S']
const MON = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec']
function CalendarHeatmap({ year, month, dayMap, onPrev, onNext, canPrev, canNext, onHoverDay, hoverKey, unit = 'SOL' }) {
  // Build the grid: leading blanks for the first day's weekday, then days.
  const first = new Date(year, month, 1)
  const startDow = first.getDay()
  const daysInMonth = new Date(year, month + 1, 0).getDate()
  // Max abs P/L this month → color intensity scale.
  let maxAbs = 0
  for (let d = 1; d <= daysInMonth; d++) {
    const day = dayMap.get(`${year}-${month}-${d}`)
    if (day) maxAbs = Math.max(maxAbs, Math.abs(day.pnl))
  }
  const cells = []
  for (let i = 0; i < startDow; i++) cells.push(null)
  for (let d = 1; d <= daysInMonth; d++) cells.push(d)
  while (cells.length % 7 !== 0) cells.push(null)

  const cellFor = (d) => {
    if (d == null) return <div key={`b${Math.random()}`} />
    const key = `${year}-${month}-${d}`
    const day = dayMap.get(key)
    let bg = 'transparent', fg = C.dimmer
    if (day) {
      const intensity = maxAbs > 0 ? Math.min(1, Math.abs(day.pnl) / maxAbs) : 0
      const a = 0.18 + intensity * 0.62
      if (day.pnl > 0)      bg = `rgba(70, 212, 31, ${a})`
      else if (day.pnl < 0) bg = `rgba(239, 68, 68, ${a})`
      else                  bg = 'rgba(91, 102, 119, 0.25)'
      fg = C.txt
    }
    const isHover = hoverKey === key
    return (
      <div key={key}
        onMouseEnter={() => day && onHoverDay?.(key)}
        onMouseLeave={() => onHoverDay?.(null)}
        title={day ? `${MON[month]} ${d}: ${fmtSol(day.pnl)} ${unit} · ${day.trades} trade${day.trades !== 1 ? 's' : ''}` : ''}
        style={{
          aspectRatio: '1', borderRadius: 5, background: bg,
          border: `1px solid ${isHover ? C.cyan : (day ? 'transparent' : C.lineSoft)}`,
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
          fontSize: 13, color: fg, position: 'relative', cursor: day ? 'pointer' : 'default',
          lineHeight: 1.1,
        }}>
        <span style={{ fontWeight: day ? 700 : 400 }}>{d}</span>
        {day && <span style={{ fontSize: 10, opacity: 0.85 }}>{day.trades}</span>}
      </div>
    )
  }

  return (
    <div style={{ border: `1px solid ${C.line}`, borderRadius: 7, background: C.panel2, padding: '7px 8px' }}>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 6 }}>
        <button onClick={onPrev} disabled={!canPrev} style={navBtn(canPrev)}>‹</button>
        <div style={{ flex: 1, textAlign: 'center', color: C.txt, fontSize: 14, fontWeight: 700, letterSpacing: '.4px' }}>
          {MON[month]} {year}
        </div>
        <button onClick={onNext} disabled={!canNext} style={navBtn(canNext)}>›</button>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 4, marginBottom: 4 }}>
        {DOW.map((d, i) => <div key={i} style={{ textAlign: 'center', color: C.dim, fontSize: 10, fontWeight: 700 }}>{d}</div>)}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 4 }}>
        {cells.map(cellFor)}
      </div>
    </div>
  )
}
const navBtn = (enabled) => ({
  background: 'none', border: `1px solid ${enabled ? C.line : C.lineSoft}`,
  color: enabled ? C.txt : C.dimmer, borderRadius: 4, cursor: enabled ? 'pointer' : 'default',
  width: 22, height: 22, fontSize: 13, lineHeight: 1, padding: 0,
})

export default function WalletHistoryPanel({
  history = [], loading, publicKey, onRefresh, onSelectMint, formatCompact,
  setHistoryView,
  hideToolbar = false,
  mode = 'sol',      // 'sol' | 'futures'
  fills = [],        // Hyperliquid fills (when mode === 'futures')
  hlConnected = false, // whether the HL account is available
  isLandscape = false, // when true, stack content UNDER the calendar
}) {
  const [hoverTime, setHoverTime] = useState(null)
  const isFutures = mode === 'futures'
  const unit = isFutures ? 'USDC' : 'SOL'
  // Display name for a per-row entry: HL symbols are short (ETH), SOL mints
  // are long base58 — slice those.
  const rowName = (id) => isFutures ? id : `${id.slice(0, 4)}…${id.slice(-4)}`

  const { points, stats, dayMap, monthsAvailable } = useMemo(() => {
    const dayKey = (ts) => { const d = new Date(ts); return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}` }

    // ══ FUTURES (Hyperliquid) ══════════════════════════════════════════
    // Fills carry realized P&L directly: a CLOSING fill (dir starts with
    // "Close") has a real `closedPnl` in USDC — no FIFO pairing needed.
    // Opening fills have closedPnl 0 and don't count as closed trades. A
    // "round trip" here = one closing fill. Per-symbol leaderboard groups
    // by `symbol`. Distribution uses return % = closedPnl / notional.
    if (mode === 'futures') {
      const sorted = [...fills].sort((a, b) => (a.time || 0) - (b.time || 0))
      let cum = 0
      const pts = []
      const dm = new Map()
      const monthSet = new Set()
      const closed = []
      const perSym = new Map()
      let volume = 0, totalFee = 0

      for (const f of sorted) {
        const t = f.time || 0
        const notional = Math.abs(Number(f.notional) || 0)
        volume += notional
        totalFee += Number(f.fee) || 0
        const isClose = typeof f.dir === 'string' && f.dir.startsWith('Close')
        if (!isClose) continue
        const pnl = Number(f.closedPnl) || 0
        const k = dayKey(t)
        const md = new Date(t)
        monthSet.add(`${md.getFullYear()}-${md.getMonth()}`)
        let day = dm.get(k)
        if (!day) { day = { pnl: 0, trades: 0 }; dm.set(k, day) }
        day.trades++; day.pnl += pnl
        cum += pnl
        pts.push({ time: t, pnl: cum })
        const ret = notional > 0 ? pnl / notional : 0
        closed.push({ mint: f.symbol, pnl, ret })
        const ps = perSym.get(f.symbol) || { pnl: 0, wins: 0, losses: 0, trades: 0 }
        ps.pnl += pnl; ps.trades++
        if (pnl >= 0) ps.wins++; else ps.losses++
        perSym.set(f.symbol, ps)
      }

      const months = [...monthSet].map(s => { const [y, m] = s.split('-').map(Number); return { y, m } })
        .sort((a, b) => a.y - b.y || a.m - b.m)
      const wins = closed.filter(c => c.pnl > 0)
      const losses = closed.filter(c => c.pnl < 0)
      const grossWin = wins.reduce((s, c) => s + c.pnl, 0)
      const grossLoss = Math.abs(losses.reduce((s, c) => s + c.pnl, 0))
      const winRate = closed.length ? (wins.length / closed.length) * 100 : 0
      const avgWin = wins.length ? grossWin / wins.length : 0
      const avgLoss = losses.length ? grossLoss / losses.length : 0
      const wlRatio = avgLoss > 0 ? avgWin / avgLoss : (avgWin > 0 ? Infinity : 0)
      const profitFactor = grossLoss > 0 ? grossWin / grossLoss : (grossWin > 0 ? Infinity : 0)
      const expectancy = closed.length ? (grossWin - grossLoss) / closed.length : 0
      const distBuckets = [-50, -30, -20, -10, -5, 0, 5, 10, 20, 30, 50, 100]
      const dist = new Array(distBuckets.length + 1).fill(0)
      for (const c of closed) {
        const pct = c.ret * 100
        let bi = distBuckets.findIndex(b => pct < b)
        if (bi === -1) bi = distBuckets.length
        dist[bi]++
      }
      const tokenRows = [...perSym.entries()].map(([mint, v]) => ({ mint, ...v })).sort((a, b) => b.pnl - a.pnl)

      return {
        points: pts, dayMap: dm, monthsAvailable: months,
        stats: {
          trades: fills.length, buys: 0, sells: 0,
          buyVol: 0, sellVol: 0, net: grossWin - grossLoss,
          last: pts.length ? pts[pts.length - 1].pnl : 0,
          closedCount: closed.length,
          winCount: wins.length, lossCount: losses.length,
          winRate, avgWin, avgLoss, wlRatio, profitFactor, expectancy,
          grossWin, grossLoss, realizedRT: grossWin - grossLoss,
          volume, totalFee,
          dist, distBuckets, tokenRows,
        },
      }
    }

    // ══ SOL (swaps) ════════════════════════════════════════════════════
    const sorted = [...history].sort((a, b) => a.timestamp - b.timestamp)
    let cum = 0
    const pts = []
    let buys = 0, sells = 0, buyVol = 0, sellVol = 0
    // Per-day aggregation, keyed 'YYYY-M-D' (local time).
    const dm = new Map()
    const monthSet = new Set()
    for (const t of sorted) {
      const sol = t.solAmount || 0
      if (t.side === 'buy') { buys++; buyVol += sol }
      else if (t.side === 'sell') { sells++; sellVol += sol }
      const k = dayKey(t.timestamp)
      const md = new Date(t.timestamp)
      monthSet.add(`${md.getFullYear()}-${md.getMonth()}`)
      let day = dm.get(k)
      if (!day) { day = { pnl: 0, trades: 0 }; dm.set(k, day) }
      day.trades++
      if (sol <= 0) continue
      const delta = t.side === 'sell' ? sol : -sol
      cum += delta
      day.pnl += delta
      pts.push({ time: t.timestamp, pnl: cum })
    }
    const months = [...monthSet].map(s => { const [y, m] = s.split('-').map(Number); return { y, m } })
      .sort((a, b) => a.y - b.y || a.m - b.m)

    // ── Round-trip P&L via FIFO per mint ──────────────────────────────
    // SolCheddar logs individual swaps, not closed trades. To get real
    // win-rate / profit-factor / expectancy we pair sells against prior buys
    // of the same token (FIFO), realizing P&L per closed lot. A "trade" here
    // = one closed lot (a sell consuming buy inventory). Cost basis is the
    // SOL spent per token unit on the matched buys.
    const lots = new Map() // mint -> [{ tokens, costPerToken }]
    const closed = []      // { mint, pnl, ret } realized round trips
    const perToken = new Map() // mint -> { pnl, wins, losses, trades }
    for (const t of sorted) {
      const sol = t.solAmount || 0
      const tok = t.tokenAmount || 0
      if (sol <= 0 || tok <= 0) continue
      if (!lots.has(t.mint)) lots.set(t.mint, [])
      const q = lots.get(t.mint)
      if (t.side === 'buy') {
        q.push({ tokens: tok, costPerToken: sol / tok })
      } else if (t.side === 'sell') {
        const proceedsPerToken = sol / tok
        let remaining = tok, cost = 0, matched = 0
        while (remaining > 1e-9 && q.length) {
          const lot = q[0]
          const take = Math.min(remaining, lot.tokens)
          cost += take * lot.costPerToken
          matched += take
          lot.tokens -= take
          remaining -= take
          if (lot.tokens <= 1e-9) q.shift()
        }
        if (matched > 1e-9) {
          const proceeds = matched * proceedsPerToken
          const pnl = proceeds - cost
          const ret = cost > 0 ? pnl / cost : 0
          closed.push({ mint: t.mint, pnl, ret })
          const pt = perToken.get(t.mint) || { pnl: 0, wins: 0, losses: 0, trades: 0 }
          pt.pnl += pnl; pt.trades++
          if (pnl >= 0) pt.wins++; else pt.losses++
          perToken.set(t.mint, pt)
        }
      }
    }

    const wins = closed.filter(c => c.pnl > 0)
    const losses = closed.filter(c => c.pnl < 0)
    const grossWin = wins.reduce((s, c) => s + c.pnl, 0)
    const grossLoss = Math.abs(losses.reduce((s, c) => s + c.pnl, 0))
    const winRate = closed.length ? (wins.length / closed.length) * 100 : 0
    const avgWin = wins.length ? grossWin / wins.length : 0
    const avgLoss = losses.length ? grossLoss / losses.length : 0
    const wlRatio = avgLoss > 0 ? avgWin / avgLoss : (avgWin > 0 ? Infinity : 0)
    const profitFactor = grossLoss > 0 ? grossWin / grossLoss : (grossWin > 0 ? Infinity : 0)
    const expectancy = closed.length ? (grossWin - grossLoss) / closed.length : 0

    // Distribution of round-trip % returns into buckets for a histogram.
    const distBuckets = [-50, -30, -20, -10, -5, 0, 5, 10, 20, 30, 50, 100]
    const dist = new Array(distBuckets.length + 1).fill(0)
    for (const c of closed) {
      const pct = c.ret * 100
      let bi = distBuckets.findIndex(b => pct < b)
      if (bi === -1) bi = distBuckets.length
      dist[bi]++
    }

    // Per-token leaderboard, sorted by realized P&L.
    const tokenRows = [...perToken.entries()]
      .map(([mint, v]) => ({ mint, ...v }))
      .sort((a, b) => b.pnl - a.pnl)

    return {
      points: pts,
      dayMap: dm,
      monthsAvailable: months,
      stats: {
        trades: history.length, buys, sells,
        buyVol, sellVol, net: sellVol - buyVol,
        last: pts.length ? pts[pts.length - 1].pnl : 0,
        // round-trip metrics
        closedCount: closed.length,
        winCount: wins.length, lossCount: losses.length,
        winRate, avgWin, avgLoss, wlRatio, profitFactor, expectancy,
        grossWin, grossLoss,
        realizedRT: grossWin - grossLoss,
        dist, distBuckets,
        tokenRows,
      },
    }
  }, [history, fills, mode])

  // Which month the calendar is showing — default to the latest with trades.
  const [calMonth, setCalMonth] = useState(null)
  const activeMonth = calMonth || (monthsAvailable.length ? monthsAvailable[monthsAvailable.length - 1] : { y: new Date().getFullYear(), m: new Date().getMonth() })

  const wrap = { fontFamily: MONO, height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0, background: C.bg }

  return (
    <div style={wrap}>
      {/* Toolbar — hidden when the parent (Journal panel) provides its own
          header + refresh control. */}
      {!hideToolbar && (
        <div style={{ display: 'flex', gap: 5, padding: '6px 8px', borderBottom: `1px solid ${C.line}`, alignItems: 'center' }}>
          <button onClick={() => setHistoryView?.('wallet')}
            style={tabBtn(true)}>Wallet</button>
          <button onClick={() => setHistoryView?.('bot')}
            style={tabBtn(false)}>Bot</button>
          <span style={{ flex: 1 }} />
          <button onClick={onRefresh} title="Clear cache & re-pull from Helius"
            style={ghostBtn(C.green)}>{loading ? '···' : 'Refresh'}</button>
        </div>
      )}

      {isFutures
        ? (!hlConnected && <div style={empty}>Connect Hyperliquid to view futures history</div>)
        : (!publicKey && <div style={empty}>Connect wallet to view history</div>)}
      {isFutures
        ? (hlConnected && fills.length === 0 && !loading && <div style={empty}>No futures fills found</div>)
        : (publicKey && history.length === 0 && !loading && <div style={empty}>No swap history found</div>)}

      {((isFutures && hlConnected && fills.length > 0) || (!isFutures && publicKey && history.length > 0)) && (
        <div style={{ display: 'flex', flexDirection: isLandscape ? 'column' : 'row', flex: 1, minHeight: 0, ...(isLandscape ? { minWidth: 310 } : {}), overflowY: isLandscape ? 'auto' : 'hidden' }} className="no-scrollbar">
          {/* Left (or top, in landscape): month P/L heatmap calendar */}
          <div style={{
            // Row layout: 460 is the IDEAL, not a floor. flexShrink 0 meant a
            // narrow cell handed the calendar its full 460 and squeezed the
            // stats into a clipped column; now it gives ground down to 300
            // (below that JournalPanel stacks instead — STACK_BELOW_PX).
            width: isLandscape ? '100%' : 460,
            maxWidth: 460,
            minWidth: isLandscape ? 0 : 300,
            boxSizing: 'border-box',
            flexShrink: isLandscape ? 0 : 1,
            padding: '8px',
            ...(isLandscape ? {} : { overflowY: 'auto' }),
          }} className="no-scrollbar">
            <CalendarHeatmap
              year={activeMonth.y} month={activeMonth.m} dayMap={dayMap} unit={unit}
              hoverKey={null}
              onHoverDay={() => {}}
              canPrev={monthsAvailable.length > 0 && (activeMonth.y > monthsAvailable[0].y || (activeMonth.y === monthsAvailable[0].y && activeMonth.m > monthsAvailable[0].m))}
              canNext={monthsAvailable.length > 0 && (activeMonth.y < monthsAvailable[monthsAvailable.length-1].y || (activeMonth.y === monthsAvailable[monthsAvailable.length-1].y && activeMonth.m < monthsAvailable[monthsAvailable.length-1].m))}
              onPrev={() => { const m = activeMonth.m - 1; setCalMonth(m < 0 ? { y: activeMonth.y - 1, m: 11 } : { y: activeMonth.y, m }) }}
              onNext={() => { const m = activeMonth.m + 1; setCalMonth(m > 11 ? { y: activeMonth.y + 1, m: 0 } : { y: activeMonth.y, m }) }}
            />
          </div>

          {/* Right (or below, in landscape): summary, trade stats, P/L curve, trade list */}
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, ...(isLandscape ? {} : { overflowY: 'auto' }) }} className="no-scrollbar">

          {/* Summary bar */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 7, padding: '8px' }}>
            <Kpi label="Realized" val={`${fmtSol(stats.realizedRT)}`} note={`${unit} · closed`} tone={stats.realizedRT >= 0 ? 'good' : 'bad'} />
            {isFutures
              ? <Kpi label="Cum P/L" val={`${fmtSol(stats.last)}`} note={unit} tone={stats.last >= 0 ? 'good' : 'bad'} />
              : <Kpi label="Net flow" val={`${fmtSol(stats.last)}`} note={unit} tone={stats.last >= 0 ? 'good' : 'bad'} />}
            <Kpi label="Round trips" val={stats.closedCount} note={`${stats.winCount}W / ${stats.lossCount}L`} />
            {isFutures
              ? <Kpi label="Volume" val={(stats.volume || 0).toFixed(0)} note={`${unit} · ${(stats.totalFee||0).toFixed(2)} fees`} />
              : <Kpi label="Volume" val={(stats.buyVol + stats.sellVol).toFixed(1)} note="SOL traded" />}
          </div>

          {/* Overall trade statistics */}
          <div style={{ padding: '0 8px 8px' }}>
            <div style={{ border: `1px solid ${C.line}`, borderRadius: 7, background: C.panel2, padding: '8px 10px' }}>
              <div style={{ color: C.dim, fontSize: 9, letterSpacing: '.6px', textTransform: 'uppercase', marginBottom: 8 }}>
                Overall Trade Statistics
              </div>
              <div style={{ display: 'flex', gap: 14, alignItems: 'center' }}>
                <Ring pct={stats.winRate} label="Win Rate" color={C.green}
                  sub={`${stats.winCount}/${stats.closedCount}`} />
                <div style={{ flex: 1, display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '10px 8px' }}>
                  <StatCell label="Avg Win" value={`+${stats.avgWin.toFixed(3)}`} tone="good" />
                  <StatCell label="Avg Loss" value={`-${stats.avgLoss.toFixed(3)}`} tone="bad" />
                  <StatCell label="W/L Ratio" value={fmtRatio(stats.wlRatio)} />
                  <StatCell label="Profit Factor" value={fmtRatio(stats.profitFactor)} tone={stats.profitFactor >= 1 ? 'good' : 'bad'} />
                  <StatCell label="Expectancy" value={`${stats.expectancy >= 0 ? '+' : ''}${stats.expectancy.toFixed(3)}`} tone={stats.expectancy >= 0 ? 'good' : 'bad'} />
                  <StatCell label="Gross W / L" value={`${stats.grossWin.toFixed(1)}/${stats.grossLoss.toFixed(1)}`} />
                </div>
              </div>
              {stats.closedCount > 0 && (
                <div style={{ marginTop: 10 }}>
                  <div style={{ color: C.dim, fontSize: 8.5, letterSpacing: '.5px', textTransform: 'uppercase', marginBottom: 4 }}>
                    Distribution of returns (%)
                  </div>
                  <DistHistogram dist={stats.dist} buckets={stats.distBuckets} />
                </div>
              )}
            </div>
          </div>

          {/* Top / bottom tokens by realized P&L */}
          {stats.tokenRows.length > 0 && (
            <div style={{ padding: '0 8px 8px' }}>
              <div style={{ border: `1px solid ${C.line}`, borderRadius: 7, background: C.panel2, padding: '8px 10px' }}>
                <div style={{ color: C.dim, fontSize: 9, letterSpacing: '.6px', textTransform: 'uppercase', marginBottom: 6 }}>
                  Top / Bottom {isFutures ? 'Symbols' : 'Tokens'} · realized {unit}
                </div>
                {[...stats.tokenRows.slice(0, 3), ...(stats.tokenRows.length > 6 ? stats.tokenRows.slice(-3) : stats.tokenRows.slice(3))].map(r => (
                  <div key={r.mint} onClick={() => onSelectMint?.(r.mint)}
                    style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '3px 0', cursor: 'pointer', fontSize: 11 }}>
                    <span style={{ color: C.txt }}>{rowName(r.mint)}</span>
                    <span style={{ flex: 1 }} />
                    <span style={{ color: C.dim, fontSize: 9 }}>{r.trades}t · {r.wins}W</span>
                    <span style={{ color: r.pnl >= 0 ? C.green : C.red, fontWeight: 700, minWidth: 56, textAlign: 'right' }}>
                      {fmtSol(r.pnl)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* P/L curve */}
          <div style={{ padding: '0 8px 8px' }}>
            <div style={{
              border: `1px solid ${C.line}`, borderRadius: 7, background: C.panel2,
              height: 132, padding: '6px 8px 2px', position: 'relative',
            }}>
              <div style={{ color: C.dim, fontSize: 9, letterSpacing: '.6px', textTransform: 'uppercase', marginBottom: 2 }}>
                Cumulative realized P/L · {unit}
              </div>
              <div style={{ position: 'absolute', inset: '22px 8px 4px' }}>
                {points.length >= 2
                  ? <PnlChart points={points} onHoverTime={setHoverTime} unit={unit} />
                  : <div style={{ ...empty, padding: '24px 0', fontSize: 11 }}>Need 2+ SOL trades to chart</div>}
              </div>
            </div>
          </div>

          {/* Trade list */}
          <div style={{ borderTop: `1px solid ${C.lineSoft}` }}>
            {isFutures && [...fills].sort((a,b)=>(b.time||0)-(a.time||0)).map(f => {
              const isClose = typeof f.dir === 'string' && f.dir.startsWith('Close')
              const pnl = Number(f.closedPnl) || 0
              const sideCol = f.side === 'buy' ? C.green : C.red
              const hot = hoverTime === f.time
              return (
                <div key={f.tid}
                  onMouseEnter={() => setHoverTime(f.time)}
                  onMouseLeave={() => setHoverTime(null)}
                  title={`${f.dir} ${f.symbol}\n${f.qty} @ ${f.price}`}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 8, padding: '5px 10px',
                    borderBottom: `1px solid ${C.lineSoft}`, fontSize: 11,
                    background: hot ? '#0e1622' : 'transparent', transition: 'background .1s',
                  }}>
                  <span style={{
                    color: sideCol, fontWeight: 700, fontSize: 9, minWidth: 30,
                    border: `1px solid ${sideCol}44`, borderRadius: 3, padding: '1px 4px', textAlign: 'center',
                  }}>{f.side.toUpperCase()}</span>
                  <span style={{ color: C.txt, fontWeight: 600 }}>{f.symbol}</span>
                  <span style={{ color: C.dim, fontSize: 10 }}>{f.dir}</span>
                  <span style={{ flex: 1 }} />
                  {isClose && (
                    <span style={{ color: pnl >= 0 ? C.green : C.red, fontWeight: 700 }}>
                      {fmtSol(pnl)} {unit}
                    </span>
                  )}
                </div>
              )
            })}
            {!isFutures && [...history].map(t => {
              const mintShort = `${t.mint.slice(0, 4)}…${t.mint.slice(-4)}`
              const descMatch = t.description?.match(/for [\d,.]+ ([A-Za-z0-9_]+)/) || t.description?.match(/swapped [\d,.]+ ([A-Za-z0-9_]+)/)
              const rawSymbol = t.side === 'buy' ? descMatch?.[1] : (t.description?.match(/swapped [\d,.]+ ([A-Za-z0-9_]+)/)?.[1])
              const symbol = rawSymbol && rawSymbol.length <= 12 ? rawSymbol : null
              const displayName = symbol || mintShort
              const isBuy = t.side === 'buy'
              const sideCol = isBuy ? C.green : t.side === 'sell' ? C.red : C.cyan
              const hot = hoverTime === t.timestamp
              return (
                <div key={t.signature}
                  onClick={() => onSelectMint?.(t.mint)}
                  onMouseEnter={() => setHoverTime(t.timestamp)}
                  onMouseLeave={() => setHoverTime(null)}
                  title={`${t.signature}\n${t.mint}`}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 8, padding: '5px 10px',
                    borderBottom: `1px solid ${C.lineSoft}`, cursor: 'pointer', fontSize: 11,
                    background: hot ? '#0e1622' : 'transparent', transition: 'background .1s',
                  }}>
                  <span style={{
                    color: sideCol, fontWeight: 700, fontSize: 9, minWidth: 30,
                    border: `1px solid ${sideCol}44`, borderRadius: 3, padding: '1px 4px', textAlign: 'center',
                  }}>{t.side.toUpperCase()}</span>
                  <span style={{ color: C.txt, fontWeight: 600 }}>
                    {t.solAmount > 0 ? `${t.solAmount.toFixed(2)} SOL` : formatCompact?.(t.tokenAmount)}
                  </span>
                  <span style={{ flex: 1 }} />
                  <span style={{ color: C.dim, fontSize: 10 }}>{displayName}</span>
                </div>
              )
            })}
          </div>
          </div>{/* right column */}
        </div>
      )}
    </div>
  )
}

const tabBtn = active => ({
  background: active ? '#13202a' : 'none',
  border: `1px solid ${active ? C.green : C.line}`,
  color: active ? C.green : C.dim,
  padding: '3px 10px', borderRadius: 4, cursor: 'pointer',
  fontFamily: MONO, fontSize: 11, fontWeight: 700, letterSpacing: '.4px', textTransform: 'uppercase',
})
const ghostBtn = col => ({
  background: 'none', border: `1px solid ${col}`, color: col,
  padding: '3px 10px', borderRadius: 4, cursor: 'pointer',
  fontFamily: MONO, fontSize: 10, letterSpacing: '.4px',
})
const empty = { color: C.dim, textAlign: 'center', padding: '40px 0', fontSize: 12, fontFamily: MONO }